import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import * as moment from 'moment-timezone';
import { ConfigModel, ConfigRestrictionModel } from 'app/models/config-model';

type ConfigFrontendType = 'TEXT' | 'NUMBER' | 'SELECT' | 'DATE' | 'DATETIME' | 'FILE';

@Component({
  selector: 'app-config-field',
  templateUrl: './config-field.component.html',
  styleUrls: ['./config-field.component.scss']
})
export class ConfigFieldComponent implements OnChanges {

  @Input() config!: ConfigModel;
  @Input() value: any = '';
  @Output() valueChange = new EventEmitter<any>();

  currentValue: any = '';
  fileName = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] || changes['config']) {
      this.currentValue = this.formatValueForView(this.value);
      if (this.frontendType !== 'FILE') {
        this.fileName = '';
      }
    }
  }

  get restriction(): ConfigRestrictionModel {
    return this.config?.restriction ?? {};
  }

  get frontendType(): ConfigFrontendType {
    const rawType = (this.restriction.frontend || this.config?.type || 'TEXT')
      .toString()
      .trim()
      .toUpperCase();

    switch (rawType) {
      case 'NUMBER':
        return 'NUMBER';
      case 'SELECT':
        return 'SELECT';
      case 'DATE':
        return 'DATE';
      case 'DATETIME':
      case 'DATETIME-LOCAL':
      case 'DATETIME_LOCAL':
        return 'DATETIME';
      case 'FILE':
        return 'FILE';
      case 'TEXTAREA':
      case 'INPUT':
      case 'STRING':
      case 'TEXT':
      default:
        return 'TEXT';
    }
  }

  get options(): string[] {
    return Array.isArray(this.restriction.options) ? this.restriction.options : [];
  }

  get min(): number | null {
    return typeof this.restriction.min === 'number' ? this.restriction.min : null;
  }

  get max(): number | null {
    return typeof this.restriction.max === 'number' ? this.restriction.max : null;
  }

  get isFullSpan(): boolean {
    return this.frontendType === 'FILE' || this.frontendType === 'TEXT';
  }

  get acceptTypes(): string | null {
    return this.frontendType === 'FILE' && this.options.length ? this.options.join(',') : null;
  }

  get imagePreviewSrc(): string | null {
    if (this.frontendType !== 'FILE' || typeof this.currentValue !== 'string') {
      return null;
    }

    const trimmedValue = this.currentValue.trim();
    if (trimmedValue.startsWith('data:image/')) {
      return trimmedValue;
    }

    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(trimmedValue)) {
      return trimmedValue;
    }

    return null;
  }

  onTextChange(value: string) {
    const normalized = this.normalizeText(value);
    this.currentValue = normalized;
    this.valueChange.emit(normalized);
  }

  onNumberChange(value: string | number) {
    const normalized = this.normalizeNumber(value);
    this.currentValue = normalized;
    this.valueChange.emit(normalized);
  }

  onSelectChange(value: string) {
    const normalized = this.normalizeSelect(value);
    this.currentValue = normalized;
    this.valueChange.emit(normalized);
  }

  onDateChange(value: string) {
    const normalized = this.normalizeDate(value);
    this.currentValue = normalized;
    this.valueChange.emit(normalized);
  }

  onDatetimeChange(value: string) {
    const normalized = this.normalizeDatetime(value);
    this.currentValue = normalized;
    this.valueChange.emit(normalized);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.fileName = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      this.currentValue = result;
      this.fileName = file.name;
      this.valueChange.emit(result);
    };
    reader.readAsDataURL(file);
  }

  private normalizeText(value: string): string {
    const rawValue = (value ?? '').toString();
    if (this.max != null) {
      return rawValue.slice(0, this.max);
    }
    return rawValue;
  }

  private normalizeNumber(value: string | number): number | null {
    if (value === '' || value == null) {
      return null;
    }

    let normalized = Number(value);
    if (Number.isNaN(normalized)) {
      return null;
    }
    if (this.min != null) {
      normalized = Math.max(normalized, this.min);
    }
    if (this.max != null) {
      normalized = Math.min(normalized, this.max);
    }
    return normalized;
  }

  private normalizeSelect(value: string): string {
    if (!this.options.length) {
      return value ?? '';
    }
    return this.options.includes(value) ? value : this.options[0];
  }

  private normalizeDate(value: string): string {
    if (!value) {
      return '';
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  }

  private normalizeDatetime(value: string): string {
    if (!value) {
      return '';
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : '';
  }

  private formatValueForView(value: any): any {
    if (value == null) {
      return '';
    }

    switch (this.frontendType) {
      case 'NUMBER':
        return this.normalizeNumber(value);
      case 'SELECT':
        return this.normalizeSelect(String(value));
      case 'DATE':
        return this.normalizeDate(String(value));
      case 'DATETIME':
        return this.normalizeDatetime(String(value));
      case 'FILE':
        return value;
      case 'TEXT':
      default:
        return this.normalizeText(String(value));
    }
  }
}
